import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { flexbox, fontSize } from 'styled-system';
import styled from 'styled-components';
import { Box } from './common';
import { colors } from '@styles/theme';
import { HeadLine } from './Typography';

const Wrapper = styled(Box)`
  position: relative;
  z-index: 2;
`;

const SegmentButton = styled('button')`
  height: 36px;
  display: flex;
  background: transparent;
  border: 0;
  cursor: pointer;
  &:enabled {
    color: ${props => (props.selected ? colors.darkGold : colors.lightGold)};
  }

  outline: 0;
  ${flexbox}
  ${fontSize}
`;

const ActiveBar = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  border-bottom: 1px solid ${colors.darkGold};
  height: 1px;
`;

const transitionTime = 200;
const transitionStyle = `left ${transitionTime}ms, right ${transitionTime}ms`;

function AnimatedTabs(props) {
  const { segments, selectedIndex, onChange, ...restProps } = props;
  const [previousIndex, setpreviousIndex] = useState(selectedIndex);
  const [tabSizes, setTabSizes] = useState([]);

  const rootRef = useRef(null);

  const childRefs = segments.map(() => useRef(null));

  const getSizes = () => {
    if (!rootRef || !rootRef.current) {
      return;
    }

    const elm = rootRef.current;

    const rootBounds = elm.getBoundingClientRect();

    const sizes = [];

    for (let i = 0; i < childRefs.length; i += 1) {
      const el = childRefs[i].current;
      const bounds = el.getBoundingClientRect();

      const left = bounds.left - rootBounds.left;
      const right = rootBounds.right - bounds.right;

      sizes[i] = { left, right };
    }

    setTabSizes(sizes);
  };

  const getUnderlineStyle = () => {
    if (!tabSizes || tabSizes.length === 0) {
      return { left: '0', right: '100%' };
    }

    const size = tabSizes[selectedIndex];

    const width = 100 / tabSizes.length;

    return {
      left: `${size.left}px`,
      right: `${size.right}px`,
      transition: transitionStyle,
      width: `${width}%`
    };
  };

  const handleChangeSegment = (segment, segmentIndex) => {
    if (previousIndex === segmentIndex || !onChange) {
      return;
    }

    onChange(segment, segmentIndex);
    setpreviousIndex(segmentIndex);
  };

  useEffect(() => {
    getSizes();
  }, [rootRef.current]);

  return (
    <Wrapper width={1} {...restProps} ref={rootRef}>
      <Box display="flex" flexDirection="row" justifyContent="center">
        {segments.map((item, index) => {
          let selected = index === selectedIndex;
          let key = item;
          let itemLabel = item;

          return (
            <SegmentButton
              flex={1}
              display="flex"
              fontSize={1}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              key={key}
              selected={selected}
              ref={childRefs[index]}
              onClick={
                !selected
                  ? () => {
                      handleChangeSegment(item, index);
                    }
                  : undefined
              }
            >
              <HeadLine>{itemLabel}</HeadLine>
            </SegmentButton>
          );
        })}
      </Box>
      <ActiveBar style={getUnderlineStyle()} />
    </Wrapper>
  );
}

AnimatedTabs.propTypes = {
  segments: PropTypes.array,
  selectedIndex: PropTypes.number,
  onChange: PropTypes.func
};

export default AnimatedTabs;
